import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Plus, Search, Package, Clock, CheckCircle2, Trash2, X, Loader2,
    ArrowUpRight, Tag, AlertTriangle, TrendingUp, BarChart3, Sparkles, Star,
    RefreshCw, ChevronDown, ChevronUp, ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import ExclusiveOffersPopup from '../../components/ExclusiveOffersPopup';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';

const CATEGORIES = [
    'Eletronicos', 'Informatica', 'Celulares', 'Casa & Cozinha', 'Moda', 'Beleza',
    'Esportes', 'Games', 'Livros', 'Brinquedos', 'Alimentos', 'Ferramentas', 'Automotivo', 'Outro'
];
const STATUS_MAP = {
    pending: { label: 'Pendente', color: '#f59e0b' },
    found: { label: 'Encontrado', color: '#22c55e' },
    expired: { label: 'Expirado', color: '#ef4444' },
};

export default function OfferRequestsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [requests, setRequests] = useState([]);
    const [smartCats, setSmartCats] = useState({ categories: [], total_requests: 0 });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ product_name: '', category: '', max_price: '', description: '' });
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [viewMode, setViewMode] = useState('pedidos');
    const [showOffersPopup, setShowOffersPopup] = useState(false);
    const [preferences, setPreferences] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [lastResult, setLastResult] = useState(null);   // resultado da busca do pedido recem-criado
    const [expanded, setExpanded] = useState(null);       // request_id com as ofertas abertas na tabela
    const [researching, setResearching] = useState(null); // request_id sendo rebuscado
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const OFFERS_MINI_TOUR_STEPS = [
        { target: '[data-testid="new-request-btn"]', title: 'Novo Pedido', content: 'Solicite produtos específicos. O sistema busca as melhores ofertas para você.' },
        { target: '[data-testid="requests-view-tabs"]', title: 'Visualizações', content: 'Alterne entre Pedidos, Categorias Inteligentes e suas Preferências de Ofertas.' },
        { target: '[data-testid="requests-kpis"]', title: 'Indicadores', content: 'Acompanhe o total de pedidos, pendentes e ofertas encontradas.' },
    ];

    // Check if should show offers popup (5s after mini tour)
    useEffect(() => {
        const checkPopup = async () => {
            try {
                const res = await fetch(`${API}/offer-preferences`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    const data = await res.json();
                    setPreferences(data);
                    // Show popup if: eligible, not saved, not recently deferred
                    if (data.eligible && data.popup_status !== 'saved') {
                        if (data.popup_status === 'deferred' && data.popup_deferred_until) {
                            const deferUntil = new Date(data.popup_deferred_until);
                            if (deferUntil > new Date()) return; // Still deferred
                        }
                        // Wait for mini tour to finish, then 5s delay
                        const miniTourDone = localStorage.getItem('sv_ctour_offers-page');
                        const delay = miniTourDone ? 2000 : 15000; // 2s if tour done, 15s if tour needs to play first
                        setTimeout(() => setShowOffersPopup(true), delay);
                    }
                }
            } catch {}
        };
        checkPopup();
    }, []);

    // Fetch recommendations
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/offer-recommendations`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    const data = await res.json();
                    setRecommendations(data.deals || []);
                }
            } catch {}
        })();
    }, [showOffersPopup]);

    const fetchRequests = useCallback(async () => {
        try {
            const res = await fetch(`${API}/offer-requests`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setRequests(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    const fetchSmartCats = useCallback(async () => {
        try {
            const res = await fetch(`${API}/smart-categories`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setSmartCats(await res.json());
        } catch {}
    }, []);

    useEffect(() => { fetchRequests(); fetchSmartCats(); }, [fetchRequests, fetchSmartCats]);

    useEffect(() => {
        const action = searchParams.get('action');
        const tab = searchParams.get('tab');
        if (action === 'novo_pedido') { setForm({ product_name: '', category: '', max_price: '', description: '' }); setShowModal(true); setSearchParams({}); }
        if (tab === 'categorias') setViewMode('categorias');
        if (tab === 'pedidos') setViewMode('pedidos');
    }, [searchParams]);

    const handleSave = async () => {
        if (!form.product_name.trim()) { toast.error('Nome do produto obrigatorio'); return; }
        setSaving(true);
        setLastResult(null);
        try {
            const body = { ...form, max_price: form.max_price ? parseFloat(form.max_price) : null };
            const res = await fetch(`${API}/offer-requests`, { method: 'POST', headers, body: JSON.stringify(body) });
            if (res.ok) {
                // O backend ja busca na hora: mostra as ofertas achadas em vez de
                // so fechar o modal com um "pedido criado" que nao entrega nada.
                const data = await res.json();
                const found = (data.matches || []).length;
                setLastResult(data);
                if (found) toast.success(`${found} oferta(s) encontrada(s)`);
                else toast.info('Nenhuma oferta agora — o pedido ficou pendente e avisamos quando aparecer');
                fetchRequests(); fetchSmartCats();
            }
            else toast.error('Erro ao criar');
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const rerunSearch = async (id) => {
        setResearching(id);
        try {
            const res = await fetch(`${API}/offer-requests/${id}/search`, { method: 'POST', headers });
            if (!res.ok) { toast.error('Erro ao buscar'); return; }
            const data = await res.json();
            const found = (data.matches || []).length;
            toast[found ? 'success' : 'info'](found ? `${found} oferta(s) encontrada(s)` : 'Ainda sem oferta pra esse pedido');
            setExpanded(found ? id : null);
            fetchRequests();
        } catch { toast.error('Erro de conexão'); } finally { setResearching(null); }
    };

    const ORIGIN_LABEL = {
        deals: 'já publicadas no site',
        discoveries: 'garimpadas pelo crawler',
        live: 'buscadas ao vivo no marketplace',
    };

    const MatchList = ({ matches, origin }) => (
        <div className="flex flex-col gap-2">
            {origin && ORIGIN_LABEL[origin] && (
                <p className="text-[11px]" style={{ color: 'var(--muted-2)' }}>Ofertas {ORIGIN_LABEL[origin]}</p>
            )}
            {matches.map((m, i) => (
                <a key={i} href={m.affiliate_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2.5 rounded-xl"
                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', textDecoration: 'none' }}>
                    {m.image_url
                        ? <img src={m.image_url} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8, background: 'var(--surface)' }} />
                        : <ShoppingBag size={20} style={{ color: 'var(--muted-2)' }} />}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{m.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-sm font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{fmt(m.current_price)}</span>
                            {m.discount_percent > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>-{m.discount_percent}%</span>
                            )}
                            <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{m.store_name}</span>
                            {m.exact_match === false && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>aproximado</span>
                            )}
                        </div>
                    </div>
                    <ArrowUpRight size={14} style={{ color: 'var(--muted-2)' }} />
                </a>
            ))}
        </div>
    );

    const updateStatus = async (id, status) => {
        await fetch(`${API}/offer-requests/${id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
        toast.success('Status atualizado');
        fetchRequests(); fetchSmartCats();
    };

    const deleteRequest = async (id) => {
        if (!window.confirm('Excluir pedido?')) return;
        await fetch(`${API}/offer-requests/${id}`, { method: 'DELETE', headers });
        toast.success('Excluido');
        fetchRequests(); fetchSmartCats();
    };

    const filtered = requests.filter(r => {
        if (filterStatus !== 'all' && r.status !== filterStatus) return false;
        if (searchText && !r.product_name.toLowerCase().includes(searchText.toLowerCase())) return false;
        return true;
    });

    const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

    return (
        <div className="sv-page" data-testid="offer-requests-page">
            <ContextualTour pageKey="offers-page" steps={OFFERS_MINI_TOUR_STEPS} />
            {showOffersPopup && <ExclusiveOffersPopup onClose={() => setShowOffersPopup(false)} onSaved={() => setPreferences(prev => prev ? { ...prev, popup_status: 'saved' } : prev)} />}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Pedidos de Oferta</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Gerencie pedidos, categorias inteligentes e preferências</p>
                </div>
                <div className="flex gap-2">
                    {preferences?.eligible && (
                        <button className="sv-btn-system flex items-center gap-1.5" onClick={() => setShowOffersPopup(true)} data-testid="open-preferences-btn" style={{ border: '1px solid rgba(var(--sv-accent-rgb), 0.3)', color: 'var(--sv-accent)' }}>
                            <Sparkles size={14} /> Preferências
                        </button>
                    )}
                    <button className="sv-btn-gold-system sv-btn-system" onClick={() => { setForm({ product_name: '', category: '', max_price: '', description: '' }); setShowModal(true); }} data-testid="new-request-btn">
                        <Plus size={14} /> Novo Pedido
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="sv-kpi-grid" data-testid="requests-kpis">
                <div className="sv-kpi"><span className="sv-kpi-label">Total Pedidos</span><span className="sv-kpi-value">{requests.length}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Pendentes</span><span className="sv-kpi-value" style={{ color: '#f59e0b' }}>{requests.filter(r => r.status === 'pending').length}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Encontrados</span><span className="sv-kpi-value" style={{ color: '#22c55e' }}>{requests.filter(r => r.status === 'found').length}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Categorias Ativas</span><span className="sv-kpi-value">{smartCats.categories.filter(c => c.requests > 0).length}</span></div>
            </div>

            {/* Tabs view */}
            <div className="sv-tabs" data-testid="requests-view-tabs">
                <button className={`sv-tab ${viewMode === 'pedidos' ? 'active' : ''}`} onClick={() => setViewMode('pedidos')} data-testid="view-pedidos"><Package size={12} /> Pedidos</button>
                <button className={`sv-tab ${viewMode === 'categorias' ? 'active' : ''}`} onClick={() => setViewMode('categorias')} data-testid="view-categorias"><BarChart3 size={12} /> Categorias Inteligentes</button>
                {preferences?.eligible && (
                    <button className={`sv-tab ${viewMode === 'recomendadas' ? 'active' : ''}`} onClick={() => setViewMode('recomendadas')} data-testid="view-recomendadas"><Star size={12} /> Recomendadas</button>
                )}
            </div>

            {/* Pedidos view */}
            {viewMode === 'pedidos' && (
                <div>
                    <div className="sv-card-system mb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative flex-1 min-w-[180px]">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-2)' }} />
                                <input type="text" placeholder="Buscar pedido..." value={searchText} onChange={e => setSearchText(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="request-search" />
                            </div>
                            <div className="flex gap-2">
                                {[['all', 'Todos'], ['pending', 'Pendentes'], ['found', 'Encontrados'], ['expired', 'Expirados']].map(([k, l]) => (
                                    <button key={k} className={`sv-subnav-chip ${filterStatus === k ? 'active' : ''}`} onClick={() => setFilterStatus(k)}>{l}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="sv-card-system" style={{ padding: 0, overflow: 'hidden' }}>
                        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                            <span className="sv-card-label">Pedidos ({filtered.length})</span>
                        </div>
                        {loading ? (
                            <div className="sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
                        ) : filtered.length === 0 ? (
                            <div className="sv-empty-state"><Package /><p className="sv-empty-state-title">Nenhum pedido</p><p className="sv-empty-state-desc">Crie pedidos de ofertas</p></div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="sv-data-table" data-testid="requests-table">
                                    <thead><tr><th>Produto</th><th>Categoria</th><th>Preco Max</th><th>Status</th><th>Solicitante</th><th>Data</th><th style={{ width: 100 }}></th></tr></thead>
                                    <tbody>
                                        {filtered.map(r => {
                                            const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
                                            const matches = r.matches || [];
                                            return (
                                              <React.Fragment key={r.request_id}>
                                                <tr>
                                                    <td>
                                                        <div>
                                                            <span className="font-medium">{r.product_name}</span>
                                                            {matches.length > 0 && (
                                                                <button className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                                                                    style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: 'none', cursor: 'pointer' }}
                                                                    onClick={() => setExpanded(expanded === r.request_id ? null : r.request_id)}
                                                                    data-testid={`toggle-matches-${r.request_id}`}>
                                                                    {matches.length} oferta{matches.length > 1 ? 's' : ''}
                                                                    {expanded === r.request_id ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                                                                </button>
                                                            )}
                                                            {r.description && <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-2)' }}>{r.description}</p>}
                                                        </div>
                                                    </td>
                                                    <td><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted)' }}>{r.category || '-'}</span></td>
                                                    <td style={{ color: r.max_price ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }}>{r.max_price ? fmt(r.max_price) : '-'}</td>
                                                    <td><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${st.color}18`, color: st.color, border: `1px solid ${st.color}30` }}>{st.label}</span></td>
                                                    <td style={{ color: 'var(--muted)' }}>{r.user_name || 'Anonimo'}</td>
                                                    <td style={{ color: 'var(--muted-2)', fontSize: 12 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                                                    <td>
                                                        <div className="flex gap-1">
                                                            <button className="sv-btn-system" style={{ padding: '4px 6px' }} title="Buscar ofertas de novo" disabled={researching === r.request_id}
                                                                onClick={() => rerunSearch(r.request_id)} data-testid={`research-${r.request_id}`}>
                                                                <RefreshCw size={11} className={researching === r.request_id ? 'animate-spin' : ''} style={{ color: 'var(--sv-gold-primary)' }} />
                                                            </button>
                                                            {r.status === 'pending' && <button className="sv-btn-system" style={{ padding: '4px 6px' }} title="Marcar como encontrado" onClick={() => updateStatus(r.request_id, 'found')}><CheckCircle2 size={11} style={{ color: '#22c55e' }} /></button>}
                                                            {r.status === 'pending' && <button className="sv-btn-system" style={{ padding: '4px 6px' }} title="Expirar" onClick={() => updateStatus(r.request_id, 'expired')}><AlertTriangle size={11} style={{ color: '#f59e0b' }} /></button>}
                                                            <button className="sv-btn-system" style={{ padding: '4px 6px', color: '#ef4444' }} onClick={() => deleteRequest(r.request_id)}><Trash2 size={11} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expanded === r.request_id && matches.length > 0 && (
                                                    <tr>
                                                        <td colSpan={7} style={{ background: 'var(--surface)', padding: 12 }}>
                                                            <MatchList matches={matches} origin={r.match_origin} />
                                                        </td>
                                                    </tr>
                                                )}
                                              </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Categorias Inteligentes */}
            {viewMode === 'categorias' && (
                <div className="sv-card-system" data-testid="smart-categories-view">
                    <div className="sv-card-header"><span className="sv-card-label">Ranking de Categorias por Demanda</span></div>
                    <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>As categorias são ranqueadas pela quantidade de pedidos + ofertas disponíveis. Use esse ranking para priorizar quais ofertas publicar e enviar.</p>
                    <div className="flex flex-col gap-2">
                        {smartCats.categories.map((cat, i) => {
                            const maxScore = Math.max(1, ...smartCats.categories.map(c => c.demand_score));
                            return (
                                <div key={cat.name} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`smart-cat-${cat.name}`}>
                                    <span className="text-xs font-bold w-6 text-center" style={{ color: i < 3 ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }}>#{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{cat.name}</span>
                                            {cat.requests > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{cat.requests} pedidos</span>}
                                            {cat.deals_available > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>{cat.deals_available} ofertas</span>}
                                        </div>
                                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                                            <div className="h-full rounded-full transition-all" style={{ width: `${(cat.demand_score / maxScore) * 100}%`, background: i < 3 ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }} />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <TrendingUp size={12} style={{ color: 'var(--sv-gold-primary)' }} />
                                        <span className="text-xs font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{cat.demand_score}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recomendadas (based on preferences) */}
            {viewMode === 'recomendadas' && (
                <div data-testid="recommendations-view">
                    {preferences?.total_selections > 0 ? (
                        <>
                            <div className="sv-card-system mb-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Sparkles size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                                        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Suas Preferências</span>
                                    </div>
                                    <button className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }} onClick={() => setShowOffersPopup(true)} data-testid="edit-preferences-btn">Editar</button>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {preferences.categories?.map(c => (
                                        <span key={c.slug} className="text-[10px] px-2 py-1 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-accent)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>{c.name}</span>
                                    ))}
                                    {preferences.products?.map((p, i) => (
                                        <span key={i} className="text-[10px] px-2 py-1 rounded-full" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }}>{p.name}</span>
                                    ))}
                                </div>
                            </div>
                            {recommendations.length > 0 ? (
                                <div className="sv-card-system" style={{ padding: 0 }}>
                                    <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <span className="sv-card-label">Ofertas Recomendadas ({recommendations.length})</span>
                                    </div>
                                    <div className="flex flex-col">
                                        {recommendations.map((deal, i) => (
                                            <a key={deal.deal_id || i} href={deal.affiliate_url || deal.url || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-5 py-3 transition-colors" style={{ borderBottom: '1px solid var(--border)' }} onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'} data-testid={`recommendation-${i}`}>
                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                                    {deal.image_url ? <img src={deal.image_url} alt="" className="w-10 h-10 object-cover" /> : <Package size={16} style={{ color: 'var(--sv-gold-primary)' }} />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{deal.title}</p>
                                                    <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{deal.store_name || deal.category} {deal.source === 'preferences' && <span style={{ color: 'var(--sv-accent)' }}>• Via Preferência</span>}</p>
                                                </div>
                                                {deal.current_price && <span className="text-sm font-bold shrink-0" style={{ color: 'var(--sv-gold-primary)' }}>R$ {deal.current_price}</span>}
                                                <ArrowUpRight size={12} style={{ color: 'var(--muted-2)' }} />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="sv-card-system"><div className="sv-empty-state"><Package /><p className="sv-empty-state-title">Nenhuma oferta encontrada</p><p className="sv-empty-state-desc">Novas ofertas serão exibidas conforme ficarem disponíveis</p></div></div>
                            )}
                        </>
                    ) : (
                        <div className="sv-card-system">
                            <div className="sv-empty-state">
                                <Sparkles size={32} style={{ color: 'var(--sv-gold-primary)', opacity: 0.5 }} />
                                <p className="sv-empty-state-title">Configure suas preferências</p>
                                <p className="sv-empty-state-desc">Selecione categorias e produtos de interesse para receber ofertas personalizadas</p>
                                <button className="sv-btn-gold-system sv-btn-system mt-3" onClick={() => setShowOffersPopup(true)} data-testid="setup-preferences-btn"><Sparkles size={14} /> Configurar Preferências</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sv-card-system w-full max-w-lg" data-testid="request-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Novo Pedido de Oferta</h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowModal(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Produto / O que você procura?</label>
                                <input type="text" value={form.product_name} onChange={e => setForm({ ...form, product_name: e.target.value })} placeholder="Ex: iPhone 15, Air Fryer Philips..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="request-product-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Categoria</label>
                                <div className="flex gap-2 flex-wrap">
                                    {CATEGORIES.map(c => (<button key={c} className={`sv-subnav-chip ${form.category === c ? 'active' : ''}`} onClick={() => setForm({ ...form, category: c })}>{c}</button>))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Preco Maximo (opcional)</label>
                                <input type="number" step="0.01" value={form.max_price} onChange={e => setForm({ ...form, max_price: e.target.value })} placeholder="R$ 0.00"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="request-price-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Detalhes (opcional)</label>
                                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Cor, modelo, especificacoes..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="request-desc-input" />
                            </div>
                        </div>
                        {/* Resultado da busca feita na hora do pedido */}
                        {lastResult && (
                            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }} data-testid="request-results">
                                {(lastResult.matches || []).length > 0 ? (
                                    <>
                                        <p className="text-xs font-medium mb-2" style={{ color: '#22c55e' }}>
                                            {lastResult.matches.length} oferta(s) encontrada(s) para "{lastResult.product_name}"
                                        </p>
                                        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                            <MatchList matches={lastResult.matches} origin={lastResult.match_origin} />
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                                        Nenhuma oferta encontrada agora. O pedido ficou pendente — use o botão de buscar de novo
                                        na lista quando quiser tentar outra vez.
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => { setShowModal(false); setLastResult(null); }}>
                                {lastResult ? 'Fechar' : 'Cancelar'}
                            </button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="request-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                                {saving ? ' Buscando...' : (lastResult ? ' Buscar de novo' : ' Buscar ofertas')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
